import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../utils/api'

// Standalone print view (no app layout) — opened in a new tab; auto-triggers the
// browser print dialog so the user can "Save as PDF". Same pattern as the
// delivery / work-order print pages.

interface Metric {
  itemid: string; key: string; name: string; units: string
  min: number; avg: number; max: number; last: number | null; samples: number
}
interface Report {
  title: string; period: string; trigger: string; provider?: string | null
  status: string; error?: string | null; narrative?: string | null
  createdAt: string; createdByName?: string | null
  summary?: {
    periodLabel?: string; from?: string; till?: string
    hosts: { hostid: string; name: string; metrics: Metric[] }[]
    events: { total: number; bySeverity: Record<string, number>; top: { name: string; severity: string; host: string; at: string }[] }
    activeProblems: { total: number; bySeverity: Record<string, number> }
  } | null
}

function fmt(n: number | null, units: string) {
  if (n == null) return '—'
  const v = Math.abs(n) >= 1000 ? Math.round(n).toLocaleString() : String(n)
  return units ? `${v} ${units}` : v
}

function Markdown({ text }: { text: string }) {
  const lines = text.split('\n')
  const out: JSX.Element[] = []
  let list: string[] = []
  const inline = (s: string) =>
    s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((p, i) => {
      if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>
      if (p.startsWith('`') && p.endsWith('`')) return <code key={i}>{p.slice(1, -1)}</code>
      return <span key={i}>{p}</span>
    })
  const flush = () => {
    if (list.length) { out.push(<ul key={`u${out.length}`}>{list.map((li, i) => <li key={i}>{inline(li)}</li>)}</ul>); list = [] }
  }
  lines.forEach((raw, idx) => {
    const line = raw.replace(/\s+$/, '')
    if (/^#{1,6}\s/.test(line)) { flush(); const lvl = line.match(/^#+/)![0].length; const c = line.replace(/^#+\s/, '')
      out.push(lvl <= 2 ? <h2 key={idx}>{inline(c)}</h2> : <h3 key={idx}>{inline(c)}</h3>) }
    else if (/^\s*[-*]\s+/.test(line)) list.push(line.replace(/^\s*[-*]\s+/, ''))
    else if (/^>\s?/.test(line)) { flush(); out.push(<blockquote key={idx}>{inline(line.replace(/^>\s?/, ''))}</blockquote>) }
    else if (line.trim() === '') flush()
    else { flush(); out.push(<p key={idx}>{inline(line)}</p>) }
  })
  flush()
  return <>{out}</>
}

export default function ZabbixReportPrintPage() {
  const { id } = useParams()
  const [report, setReport] = useState<Report | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    api.get(`/zabbix/reports/${id}`)
      .then(r => setReport(r.data))
      .catch(e => setErr(e.response?.data?.message || 'โหลดรายงานไม่ได้'))
  }, [id])

  useEffect(() => {
    if (report) { const t = setTimeout(() => window.print(), 400); return () => clearTimeout(t) }
  }, [report])

  if (err) return <div style={{ padding: 40, fontFamily: 'sans-serif' }}>{err}</div>
  if (!report) return <div style={{ padding: 40, fontFamily: 'sans-serif' }}>กำลังโหลด…</div>

  const s = report.summary

  return (
    <div className="zbx-print">
      <style>{`
        .zbx-print { max-width: 820px; margin: 0 auto; padding: 32px 28px; color: #1a1a1a;
          font-family: 'Sarabun','TH Sarabun New','Segoe UI',sans-serif; font-size: 13px; line-height: 1.6; }
        .zbx-print h1 { font-size: 20px; margin: 0 0 4px; }
        .zbx-print h2 { font-size: 15px; margin: 20px 0 6px; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
        .zbx-print h3 { font-size: 13.5px; margin: 14px 0 4px; }
        .zbx-print .meta { color: #555; font-size: 12px; margin-bottom: 4px; }
        .zbx-print table { width: 100%; border-collapse: collapse; margin: 6px 0 14px; font-size: 12px; }
        .zbx-print th, .zbx-print td { border: 1px solid #d5d5d5; padding: 4px 7px; text-align: right; }
        .zbx-print th:first-child, .zbx-print td:first-child { text-align: left; }
        .zbx-print th { background: #f3f3f3; }
        .zbx-print .key { color: #888; font-size: 10.5px; font-family: monospace; }
        .zbx-print ul { margin: 4px 0 10px; padding-left: 20px; }
        .zbx-print blockquote { margin: 6px 0; padding: 4px 10px; background: #fff8e1; border-left: 3px solid #ffca28; }
        .zbx-print code { background: #f0f0f0; padding: 0 3px; border-radius: 3px; font-size: .92em; }
        .zbx-print .pill { display: inline-block; border: 1px solid #ccc; border-radius: 10px; padding: 1px 8px; margin: 0 4px 4px 0; font-size: 11px; }
        @media print { .zbx-print { padding: 0; } @page { margin: 14mm; } }
      `}</style>

      <h1>{report.title}</h1>
      <div className="meta">
        ช่วงข้อมูล: {s?.periodLabel ?? report.period}
        {s?.from && <> ({new Date(s.from).toLocaleString('th-TH')} – {new Date(s.till!).toLocaleString('th-TH')})</>}
      </div>
      <div className="meta">
        สร้างเมื่อ: {new Date(report.createdAt).toLocaleString('th-TH', { dateStyle: 'long', timeStyle: 'short' })}
        {'  ·  '}ที่มา: {report.trigger === 'scheduled' ? 'ตัวจับเวลาอัตโนมัติ' : (report.createdByName || 'ผู้ใช้')}
        {report.provider && <>{'  ·  '}AI: {report.provider}</>}
      </div>

      {report.status === 'FAILED' && (
        <p style={{ color: '#b00' }}><strong>สถานะ: ล้มเหลว</strong> — {report.error}</p>
      )}

      {report.narrative && (
        <>
          <h2>บทวิเคราะห์จาก AI</h2>
          <Markdown text={report.narrative} />
        </>
      )}

      {s?.hosts?.length ? (
        <>
          <h2>ข้อมูลสรุป</h2>
          <div className="meta">
            จำนวน Host: {s.hosts.length} · เหตุการณ์ในช่วง: {s.events?.total ?? 0} · ปัญหาที่ยังค้าง: {s.activeProblems?.total ?? 0}
          </div>
          {s.events?.bySeverity && Object.keys(s.events.bySeverity).length > 0 && (
            <div style={{ margin: '4px 0 10px' }}>
              {Object.entries(s.events.bySeverity).map(([k, v]) => <span className="pill" key={k}>{k}: {v}</span>)}
            </div>
          )}
          {s.hosts.map(h => (
            <div key={h.hostid}>
              <h3>{h.name}</h3>
              {h.metrics.length ? (
                <table>
                  <thead><tr><th>Metric</th><th>Min</th><th>Avg</th><th>Max</th><th>Last</th><th>Samples</th></tr></thead>
                  <tbody>
                    {h.metrics.map(m => (
                      <tr key={m.itemid}>
                        <td>{m.name}<br /><span className="key">{m.key}</span></td>
                        <td>{fmt(m.min, m.units)}</td>
                        <td>{fmt(m.avg, m.units)}</td>
                        <td>{fmt(m.max, m.units)}</td>
                        <td>{fmt(m.last, m.units)}</td>
                        <td>{(m.samples ?? 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p style={{ color: '#999' }}>ไม่มี metric</p>}
            </div>
          ))}
          {s.events?.top?.length ? (
            <>
              <h3>เหตุการณ์ล่าสุด</h3>
              <ul>
                {s.events.top.map((e, i) => (
                  <li key={i}>[{e.severity}] {e.name} — {e.host} ({new Date(e.at).toLocaleString('th-TH')})</li>
                ))}
              </ul>
            </>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
